/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer').querySelector('.overlay');
    }

    /**
     * Handles 'click' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-close')) return;
      this.close();
    }

    /**
     * Opens the details element.
     */
    open() {
      this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);

      // Create event handler variables (so the bound event listeners can be removed).
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

      // Add event listeners (for while disclosure is open).
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay.addEventListener('click', this.clickHandler);
    }

    /**
     * Closes the details element.
     */
    close() {
      this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* global SideDrawer */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.init();
      // C1 (2026-10-29): the rail's colour swatches. One delegated listener, so it survives every re-render.
      this.addEventListener('click', this.handleRailSwatch.bind(this));
      /* B10A-3: the sliding box rows' edge fades (cart_rail_options_v2 prints .cart-rail__opts--v2; without it nothing matches). */
      this.addEventListener('scroll', (evt) => {
        const el = evt.target;
        if (el.classList && el.classList.contains('cart-rail__szs') && el.closest('.cart-rail__opts--v2')) this.railFade(el);
        if (el.classList && el.classList.contains('b6d-rail__list')) this.railFitSoon(el);
      }, { capture: true, passive: true });
      if (window.MutationObserver) {
        let queued = false;
        new MutationObserver(() => {
          if (queued || !this.querySelector('.cart-rail__opts--v2')) return;
          queued = true;
          requestAnimationFrame(() => { queued = false; this.railFades(); });
        }).observe(this, { childList: true, subtree: true });
      }
      this.addEventListener('on:cart-drawer:before-open', () => requestAnimationFrame(() => this.railFades()));
      this.addEventListener('on:cart-drawer:after-open', () => this.railFades());
      window.addEventListener('resize', () => { if (this.querySelector('.cart-rail__opts--v2')) this.railFades(); }, { passive: true });
      /* B11A-3: the "Complete your order" rail is only as tall as the cards seen so far (cart_rail_tight). */
      if (this.classList.contains('umbra-rail-tight')) {
        const fit = () => this.railFitAll();
        if (window.MutationObserver) {
          let fitQueued = false;
          new MutationObserver(() => {
            if (fitQueued) return;
            fitQueued = true;
            requestAnimationFrame(() => { fitQueued = false; fit(); });
          }).observe(this, { childList: true, subtree: true });
        }
        this.addEventListener('on:cart-drawer:before-open', () => requestAnimationFrame(fit));
        this.addEventListener('on:cart-drawer:after-open', fit);
        window.addEventListener('resize', fit, { passive: true });
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fit, { once: true });
        else requestAnimationFrame(fit);
      }
      /* B13A: cart_drawer_swipe_anywhere (off = no class, nothing bound). */
      if (this.classList.contains('umbra-swipe-any')) this.swipeAnywhere();
      this.bindEvents();
      this.warmScripts();
    }

    /**
     * B13A (Mete 31 Oct: "Make sure you fix the scroll on the cart drawer. It shouldn't get stuck"). The header strip
     * and the sticky checkout block sit outside the scroller (.drawer__content), and the page lock
     * (snippets/umbra-scroll-lock.liquid) cancels touchmoves there, so a thumb swipe on the bottom quarter of the phone
     * drawer moved nothing. Touch proxy: once a finger that went down there has moved over 8 px and more up/down than
     * sideways, the list follows it; on release it carries the finger's speed over the last 100 ms as momentum that
     * decays every frame and stops at the ends. A touch that did not move stays a tap; a click that ends a swipe is
     * dropped, so a swipe over Checkout never submits. Listeners sit on the drawer element, which every re-render keeps
     * (only its children are replaced). Touch events only, and hover laptops (770 px and up) skip it.
     */
    swipeAnywhere() {
      const laptop = window.matchMedia('(hover: hover) and (min-width: 770px)');
      let s = null;
      let fling = 0;
      let lastSwipe = 0;
      const stopFling = () => {
        if (fling) cancelAnimationFrame(fling);
        fling = 0;
      };
      const scrollerIn = (el) => {
        for (let t = el; t && t !== this; t = t.parentElement) {
          const oy = getComputedStyle(t).overflowY;
          if ((oy === 'auto' || oy === 'scroll') && t.scrollHeight > t.clientHeight + 1) return t;
        }
        return null;
      };
      this.addEventListener('touchstart', (e) => {
        stopFling();
        s = null;
        if (laptop.matches || !e.touches || e.touches.length !== 1) return;
        const t = e.target;
        if (!t || !t.closest || t.closest('.drawer__content, .overlay')) return;
        if (!t.closest('.drawer__header, .cart-drawer__summary')) return;
        if (scrollerIn(t)) return;
        const sc = this.querySelector('.drawer__content');
        if (!sc || sc.scrollHeight <= sc.clientHeight + 1) return;
        const p = e.touches[0];
        s = { sc, x0: p.clientX, y0: p.clientY, y: p.clientY, on: false, pts: [{ t: e.timeStamp, y: p.clientY }] };
      }, { passive: true });
      this.addEventListener('touchmove', (e) => {
        if (!s) return;
        const p = e.touches && e.touches.length === 1 ? e.touches[0] : null;
        if (!p) { s = null; return; }
        const dx = p.clientX - s.x0;
        const dy = p.clientY - s.y0;
        if (!s.on) {
          if (Math.abs(dx) <= 8 && Math.abs(dy) <= 8) return;
          if (Math.abs(dy) <= Math.abs(dx)) { s = null; return; }
          s.on = true;
        }
        if (e.cancelable) e.preventDefault();
        const max = s.sc.scrollHeight - s.sc.clientHeight;
        s.pos = Math.max(0, Math.min(max, (s.pos === undefined ? s.sc.scrollTop : s.pos) - (p.clientY - s.y)));
        s.sc.scrollTop = s.pos;
        s.y = p.clientY;
        s.pts.push({ t: e.timeStamp, y: p.clientY });
        while (s.pts.length > 2 && e.timeStamp - s.pts[0].t > 100) s.pts.shift();
      }, { passive: false });
      const end = (e) => {
        const g = s;
        s = null;
        if (!g || !g.on) return;
        /* Event times, not the clock at the handler: page-wide click listeners that run first can take hundreds of ms. */
        lastSwipe = e.timeStamp || performance.now();
        if (e.type === 'touchcancel') return;
        const a = g.pts[0];
        const b = g.pts[g.pts.length - 1];
        const dt = b.t - a.t;
        if (dt <= 0 || e.timeStamp - b.t > 100) return;
        let v = Math.max(-6, Math.min(6, (a.y - b.y) / dt));
        let prev = performance.now();
        let pos = g.sc.scrollTop;
        const step = (now) => {
          const ms = Math.min(64, now - prev);
          prev = now;
          const max = g.sc.scrollHeight - g.sc.clientHeight;
          pos = Math.max(0, Math.min(max, pos + v * ms));
          g.sc.scrollTop = pos;
          v *= Math.pow(0.996, ms);
          if (Math.abs(v) < 0.02 || pos <= 0 || pos >= max) { fling = 0; return; }
          fling = requestAnimationFrame(step);
        };
        if (Math.abs(v) >= 0.1) fling = requestAnimationFrame(step);
      };
      this.addEventListener('touchend', end, { passive: true });
      this.addEventListener('touchcancel', end, { passive: true });
      this.addEventListener('click', (e) => {
        if (!lastSwipe || (e.timeStamp || performance.now()) - lastSwipe > 400) return;
        lastSwipe = 0;
        e.preventDefault();
        e.stopPropagation();
      }, { capture: true });
      this.addEventListener('wheel', stopFling, { passive: true });
      this.addEventListener('on:cart-drawer:before-close', stopFling);
    }

    /**
     * C1 (2026-10-29): a click on a colour swatch of an "Others also bought" card shows that colour's photo, points the
     * title link at it and tells quick add which colour to open on (the same contract as ProductCard.handleSwatchChange).
     * @param {Event} evt - Click event.
     */
    handleRailSwatch(evt) {
      const sz = evt.target.closest('.cart-rail__szs .cart-rail__sz');
      if (sz) {
        this.handleRailSize(sz);
        return;
      }
      const sw = evt.target.closest('.cart-rail__sws .cart-xsell__sw');
      if (!sw) return;
      if (sw.closest('.cart-rail__opts')) {
        this.handleRailSize(null, sw);
        return;
      }
      sw.parentElement.querySelectorAll('.cart-xsell__sw').forEach((b) => {
        b.setAttribute('aria-pressed', b === sw ? 'true' : 'false');
      });
      const card = sw.closest('.card');
      if (!card) return;
      const img = card.querySelector('.card__media img');
      if (sw.dataset.img && img) {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = sw.dataset.img;
      }
      /* R5-C1: set ?variant= on the URL the card already has, so the pr_* recommendation parameters stay and no second
         query string is appended (the PDP opened on the wrong colour). */
      if (sw.dataset.variantId) {
        card.querySelectorAll('.card-link, .card__media-link').forEach((link) => {
          const u = new URL(link.getAttribute('href'), window.location.origin);
          u.searchParams.set('variant', sw.dataset.variantId);
          link.href = u.pathname + u.search + u.hash;
        });
      }
      const quick = card.querySelector('.js-quick-add');
      if (quick) quick.dataset.selectedColor = sw.dataset.value;
      /* DC2 (2026-10-30): a card with the direct Add to cart form (cart_rail_direct_add) adds the picked colour. Sold-out
         colours are disabled buttons (snippets/product-card-mini.liquid), so a click never lands on one. */
      const form = card.querySelector('product-form[data-card-direct] .js-product-form');
      if (form && sw.dataset.variantId) {
        const idInput = form.querySelector('[name="id"]');
        const skuInput = form.querySelector('[name="sku"]');
        if (idInput) idInput.value = sw.dataset.variantId;
        if (skuInput) skuInput.value = sw.dataset.sku || '';
        this.railPick(card, sw.dataset.variantId);
      }
    }

    /**
     * B10A-3 / B10A-4 (round B10): a direct rail card's pick shows that variant's price (cart_rail_options_v2: the
     * .cart-rail__pv JSON carries data-price) and checks the "shipping is on us" line again (cart_rail_ship_line: the
     * line's data-gap). Without either setting the JSON is not printed and nothing happens.
     * @param {Element} card - The rail card.
     * @param {string|number} id - The picked variant id.
     */
    railPick(card, id) {
      const pv = card.querySelector('.cart-rail__pv');
      if (!pv) return;
      if (!pv.railMap) {
        try { pv.railMap = JSON.parse(pv.textContent); } catch (e) { pv.railMap = {}; }
      }
      const e = pv.railMap[String(id)];
      if (!e) return;
      if (pv.hasAttribute('data-price')) {
        const price = card.querySelector('.price');
        const now = price && price.querySelector('.price__default .price__current');
        const was = price && price.querySelector('.price__default .price__was');
        if (now) now.innerHTML = e[1];
        if (was) was.innerHTML = e[2];
        if (price) price.classList.toggle('price--on-sale', e[2] !== '');
      }
      const line = card.querySelector('.cart-rail__ship');
      if (line) {
        const on = e[0] >= Number(line.dataset.gap);
        line.toggleAttribute('data-off', !on);
        if (on) line.removeAttribute('aria-hidden'); else line.setAttribute('aria-hidden', 'true');
      }
    }

    /**
     * B10A-3 (cart_rail_options_v2): the boxes of a rail card stay on one line and slide sideways. A 16 px fade sits on
     * each edge where more boxes wait (data-fade-start / data-fade-end, assets/cart-items.css), only while they do.
     * Called on every scroll of a box row (one capturing listener) and whenever the drawer's markup changes.
     */
    railFades() {
      this.querySelectorAll('.cart-rail__opts--v2 .cart-rail__szs').forEach((el) => this.railFade(el));
    }

    railFade(el) {
      const max = el.scrollWidth - el.clientWidth;
      const x = Math.abs(el.scrollLeft);
      el.toggleAttribute('data-fade-start', max > 1 && x > 1);
      el.toggleAttribute('data-fade-end', max > 1 && max - x > 1);
      /* B13B-1 (QA-B13 F1): the row holds a sideways swipe (overscroll-behavior-x contain, assets/cart-items.css) only
         while its boxes really overflow; boxes that fit hand the swipe to the rail, as before B10A-3. */
      el.toggleAttribute('data-slides', max > 1);
      /* boxes that wrapped under the swatches start at the start edge, in line with the swatches and the pill.
         B10G: read the first box's own top, not the row's (its 16 px tap padding reaches above the swatches' bottom
         once cart_rail_tight closes the wrapped line up to 12 px); the answer is the same in every other layout. */
      const sws = el.previousElementSibling;
      if (sws && sws.classList.contains('cart-rail__sws')) {
        el.toggleAttribute('data-own-line', (el.firstElementChild || el).getBoundingClientRect().top >= sws.getBoundingClientRect().bottom);
      }
    }

    /**
     * B11A-3 (Mete 31 Oct: "make sure the others also bought section doesn't start too far away from complete your order
     * section"). Every slide of the rail was as tall as its tallest card, so a card further along with a two-line title,
     * swatches and a "shipping is on us" line held up to 40 px of white under the cards in view. With cart_rail_tight the
     * row is now as tall as the tallest card seen so far: the cards in view at rest, and each card a swipe or an arrow
     * brings in (it grows the moment a card enters, so nothing is ever cut, and never shrinks back while the shopper
     * swipes, so "Others also bought" moves at most once per card and only down). A pick that adds or drops a card's
     * line re-measures it (ResizeObserver). A re-render starts over. Off (no umbra-rail-tight): never called.
     */
    railFitAll() {
      this.querySelectorAll('.b6d-rail--complete .b6d-rail__list').forEach((list) => {
        if (!list.railFitObs && window.ResizeObserver) {
          list.railFitObs = new ResizeObserver(() => this.railFitSoon(list));
          Array.from(list.children).forEach((li) => {
            Array.from(li.children).forEach((card) => list.railFitObs.observe(card));
          });
        }
        /* a card coming into the row's view: a scroll event alone was not enough (WebKit fired none for a scroll set
           from script, measured in Playwright), the observer fires on every change of what is in view */
        if (!list.railFitView && window.IntersectionObserver) {
          list.railFitView = new IntersectionObserver(() => this.railFitSoon(list), { root: list, threshold: 0 });
          Array.from(list.children).forEach((li) => list.railFitView.observe(li));
        }
        this.railFit(list);
      });
    }

    railFitSoon(list) {
      if (list.railFitQueued || !this.classList.contains('umbra-rail-tight')) return;
      list.railFitQueued = true;
      requestAnimationFrame(() => { list.railFitQueued = false; this.railFit(list); });
    }

    railFit(list) {
      if (!list.isConnected) return;
      const box = list.getBoundingClientRect();
      if (!box.width) return;
      const seen = list.railSeen || (list.railSeen = new Set());
      let tallest = 0;
      Array.from(list.children).forEach((li) => {
        const r = li.getBoundingClientRect();
        if (r.right > box.left + 1 && r.left < box.right - 1) seen.add(li);
        if (!seen.has(li)) return;
        Array.from(li.children).forEach((card) => { tallest = Math.max(tallest, card.getBoundingClientRect().bottom - r.top); });
      });
      if (!tallest) return;
      const cs = getComputedStyle(list);
      const pad = cs.boxSizing === 'border-box' ? parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom) : 0;
      const height = `${Math.ceil(tallest + pad)}px`;
      if (list.style.height !== height) list.style.height = height;
    }

    /**
     * B9A-1 (2026-10-31): a card with colour and size (or size alone) in one row (snippets/product-card-mini.liquid,
     * .cart-rail__opts). A size click keeps the colour; a colour click keeps the size when that pair is available, else
     * takes the colour's first available size. The sizes the new colour lacks are disabled. The direct form, the links
     * and the photo follow the picked variant. Disabled buttons never fire a click.
     * @param {?Element} sz - The size button clicked, or null.
     * @param {?Element} sw - The colour swatch clicked, or undefined.
     */
    handleRailSize(sz, sw) {
      const row = (sz || sw).closest('.cart-rail__opts');
      const card = row.closest('.card');
      let vars = [];
      try { vars = JSON.parse(row.querySelector('.cart-rail__vars').textContent); } catch (e) { return; }
      const sws = [...row.querySelectorAll('.cart-xsell__sw')];
      const szs = [...row.querySelectorAll('.cart-rail__sz')];
      const pressed = (list) => list.find((b) => b.getAttribute('aria-pressed') === 'true');
      const colourBtn = sw || pressed(sws);
      const colour = colourBtn ? colourBtn.dataset.value : null;
      const sizeBtn = sz || pressed(szs);
      let size = sizeBtn ? sizeBtn.dataset.value : null;
      const inColour = (v) => colour === null || v[2] === null || v[2] === colour;
      let v = vars.find((x) => x[4] && inColour(x) && x[3] === size);
      if (!v && sw) {
        v = vars.find((x) => x[4] && inColour(x));
        if (v) size = v[3];
      }
      if (!v) return;
      sws.forEach((b) => b.setAttribute('aria-pressed', b === colourBtn ? 'true' : 'false'));
      szs.forEach((b) => {
        b.setAttribute('aria-pressed', b.dataset.value === size ? 'true' : 'false');
        const ok = vars.some((x) => x[4] && inColour(x) && x[3] === b.dataset.value);
        b.disabled = !ok;
        if (ok) {
          b.removeAttribute('aria-disabled');
          b.setAttribute('aria-label', b.dataset.value);
        } else {
          b.setAttribute('aria-disabled', 'true');
          b.setAttribute('aria-label', `${b.dataset.value}, ${b.parentElement.dataset.soldOut || 'Sold out'}`);
        }
      });
      if (sw) {
        const img = card.querySelector('.card__media img');
        if (sw.dataset.img && img) {
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
          img.src = sw.dataset.img;
        }
      }
      card.querySelectorAll('.card-link, .card__media-link').forEach((link) => {
        const u = new URL(link.getAttribute('href'), window.location.origin);
        u.searchParams.set('variant', v[0]);
        link.href = u.pathname + u.search + u.hash;
      });
      const form = card.querySelector('product-form[data-card-direct] .js-product-form');
      if (form) {
        const idInput = form.querySelector('[name="id"]');
        const skuInput = form.querySelector('[name="sku"]');
        if (idInput) idInput.value = v[0];
        if (skuInput) skuInput.value = v[1] || '';
      }
      this.railPick(card, v[0]);
    }

    /**
     * 2026-09-23: fetches the scripts the first add-to-cart needs (cart-items, country/province selector, shipping
     * calculator; ~20 KB together) while the page is idle, so renderContents finds them present instead of loading
     * them one after another on the critical path (measured 570 ms of a 4.1 s drawer open).
     */
    warmScripts() {
      const warm = () => { this.loadScripts().catch(() => {}); };
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(warm, { timeout: 4000 });
      } else {
        setTimeout(warm, 2500);
      }
    }

    /**
     * Loads the scripts the drawer needs once: cart-items in parallel with the shipping pair (the calculator needs
     * the country/province selector first). Repeated calls share one promise; a failure resets it for a retry.
     * @returns {Promise}
     */
    loadScripts() {
      if (this.scriptsLoaded) return Promise.resolve();
      if (!this.scriptsLoading) {
        const loads = [];
        if (!document.querySelector(`script[src="${theme.scripts.cartItems}"]`)) {
          loads.push(loadScript(theme.scripts.cartItems));
        }
        if (!document.querySelector(`script[src="${theme.scripts.shippingCalculator}"]`) && this.dataset.shippingCalculator) {
          loads.push(loadScript(theme.scripts.countryProvinceSelector).then(() => loadScript(theme.scripts.shippingCalculator)));
        }
        this.scriptsLoading = Promise.all(loads)
          .then(() => { this.scriptsLoaded = true; })
          .catch((e) => { this.scriptsLoading = null; throw e; });
      }
      return this.scriptsLoading;
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('on:bfcache:load-restore', this.refreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');
      if (cartIcon) {
        cartIcon.setAttribute('role', 'button');
        cartIcon.setAttribute('aria-haspopup', 'dialog');

        cartIcon.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.open(cartIcon);
        });

        cartIcon.addEventListener('keydown', (evt) => {
          if (evt.key !== ' ') return;
          evt.preventDefault();
          this.open(cartIcon);
        });
      }
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
        this.dcbLoadedHandler = this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      this.refreshHandler = this.refresh.bind(this);
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.addEventListener('on:bfcache:load-restore', this.refreshHandler);

      // 2026-10-26 (Mete): every opening starts at the top. Reset when the open begins (the panel is still hidden or
      // sliding in) and again once the close has finished (hidden by then), so an old position never shows. The
      // timings are SideDrawer's own, untouched; nothing runs while the pointer stays inside and the user scrolls.
      this.resetScrollHandler = this.resetScroll.bind(this);
      this.addEventListener('on:cart-drawer:before-open', this.resetScrollHandler);
      this.addEventListener('on:cart-drawer:after-close', this.resetScrollHandler);
      /* While the content is still displayed (a hidden box resets nothing, and Chromium restores the old offset when
         it is shown again), and once more on the frame after the open has made it visible, hover or click alike. */
      this.addEventListener('on:cart-drawer:before-close', this.resetScrollHandler);
      this.addEventListener('on:cart-drawer:before-open', () => requestAnimationFrame(this.resetScrollHandler));
    }

    /**
     * Puts the drawer and every scroll container inside it back at scrollTop 0. Reads first, writes second, and only
     * writes the ones that moved, so it costs one layout read and no reflow loop.
     */
    resetScroll() {
      const moved = [];
      if (this.scrollTop) moved.push(this);
      this.querySelectorAll('*').forEach((el) => {
        if (el.scrollTop) moved.push(el);
      });
      moved.forEach((el) => { el.scrollTop = 0; });
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    /**
     * Handle when the section is de-selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    /**
     * Handle when the drawer is opened via an event
     * @param {object} evt - Event object.
     */
    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    /**
     * R7-C3: Shopify's wallet placeholder comes back with every re-render of the drawer footer, so the 6 s wait restarts
     * with it, as on open(). Otherwise a placeholder rendered after umbra-wallet-late was set is hidden at once, and
     * wallet buttons that arrive later grow the footer inside the open drawer.
     * R9-C3: once the wait has run out with only the placeholder showing, and no wallet button has ever replaced it in
     * this drawer, the wallet script is taken as blocked: later re-renders keep the reserve off instead of growing the
     * footer and collapsing it 6 s later on every cart update.
     */
    restartWalletWait() {
      if (this.walletBlocked && !this.walletSeen && this.classList.contains('umbra-wallet-late') &&
        this.querySelector('.dynamic-cart-btns .wallet-button-wrapper')) return;
      clearTimeout(this.walletLate);
      this.classList.remove('umbra-wallet-late');
      this.walletLate = setTimeout(() => {
        if (this.querySelector('.dynamic-cart-btns .wallet-button-wrapper')) this.walletBlocked = true;
        else if (this.querySelector('.dynamic-cart-btns')) this.walletSeen = true;
        this.classList.add('umbra-wallet-late');
      }, 6000);
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     */
    open(opener, elementToFocus) {
      /* R5-C3: the footer holds up to 151 px for the wallet buttons while Shopify's placeholder is up (cart-items.css). If no
         wallet button has replaced it 6 s after the drawer opens, the reserve ends (umbra-wallet-late hides the placeholder). */
      this.restartWalletWait();

      // Get the quick add drawer web component, if it's currently open and close it
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      // If the cart drawer is open, wait a few ms for a more optimal ux/animation
      setTimeout(() => {
        super.open(opener, elementToFocus, () => {
          if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
            CartDrawer.recalculateCssVarHeights();
          }
        });

        if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }

        if (overlay) overlay.style.transitionDelay = '';
      }, quickAddDrawer ? 200 : 0);
    }

    /**
     * Closes the drawer.
     * W2-T5b: SideDrawer.close hands focus back to the opener at once. The sticky Add to cart bar on phones hides itself
     * while the drawer is open (visibility:hidden) and only comes back a moment after the page is unlocked, so at that
     * instant it cannot take focus and focus fell to the page. Keep trying for up to 2 s, but only while nothing else
     * has focus (the page, or anything inside the drawer that is closing, such as its close button).
     * W2-T5c: the main Add to cart button is hidden by assets/umbra-atc-stepper.js about 1.4 s after a successful add (the
     * quantity stepper takes its place), so there is nothing to focus. The target is worked out on every try: the opener while it
     * is drawn, otherwise the stepper's [+] button in the opener's own form (the stepper's own hand-off rule in Stepper.render).
     * @param {Function} [callback] - Call back function to trigger after the close has completed
     */
    close(callback) {
      const opener = this.opener;
      super.close(callback);
      if (!opener || typeof opener.focus !== 'function') return;
      const target = () => {
        if (opener.isConnected && opener.getClientRects().length) return opener;
        const form = opener.closest && opener.closest('form');
        const plus = form && form.querySelector('[data-umbra-atc-stepper]:not([hidden]) [data-step="1"]');
        return plus || opener;
      };
      let tries = 0;
      const refocus = () => {
        const active = document.activeElement;
        const to = target();
        if (active === to || !opener.isConnected || ++tries > 40) return;
        if (active && active !== document.body && !this.contains(active)) return;
        to.focus({ preventScroll: true });
        setTimeout(refocus, 50);
      };
      setTimeout(refocus, 50);
    }

    /**
     * Attempts to refresh any cart items (if present). Failing that, it refreshes the entire cart
     * drawer
     * @param {boolean} [dontRefreshCartItems=false] - Prevents the refresh of cart items, and does
     * a straight refresh of the whole cart
     */
    async refresh(dontRefreshCartItems) {
      try {
        const cartItems = this.querySelector('cart-items');
        if (cartItems && !dontRefreshCartItems) {
          cartItems.refresh();
        } else {
          const response = this.getSectionsToRender().map((section) => section.section);
          const cartResponse = await fetch(`?sections=${response.join(',')}`);
          const sections = await cartResponse.json();
          this.renderContents({ sections }, false);
          CartDrawer.recalculateCssVarHeights();
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));
      }
    }

    /**
     * Renders the contents of the specified sections to update.
     * @param {object} data - Cart data object.
     * @param {boolean} [openDrawer=true] - Open the cart drawer after rendering sections.
     */
    async renderContents(data, openDrawer = true) {
      // W2-T5: remember what had focus (the Add to cart button) so closing the drawer returns focus there instead of to the page top.
      const opener = document.activeElement && document.activeElement !== document.body ? document.activeElement : null;
      await this.loadScripts();

      const restoreBar = this.holdShippingBar();
      const restorePlace = this.keepPlace();

      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) {
          el.innerHTML = CartDrawer.getElementHTML(
            data.sections[section.section],
            section.selector
          );
        }
      });

      restoreBar();
      restorePlace();
      this.restartWalletWait();

      if (openDrawer && this.getAttribute('open') === null) {
        setTimeout(() => this.open(opener));
      }
    }

    /**
     * B11-rev-1 (Mete 31 Oct: in the phone cart drawer "the others also bought was not showing up"; "It shouldn't get
     * stuck"). An Add inside the open drawer (the "Complete your order" rail or "Others also bought") re-renders the
     * whole drawer: the scroller jumped back to the top and "Others also bought" came back empty until its idle-time
     * fetch landed 2.5 to 5 s later. With the rail in the drawer (A-only settings) and the drawer open, the scroller
     * now keeps its place, the new list is fetched at once and keeps the old list's height until its cards land (at
     * most 10 s), so nothing under the shopper's finger collapses. Drawer closed or no rail: nothing changes.
     * @returns {Function} Call after the markup is replaced.
     */
    keepPlace() {
      const sc = this.querySelector('.drawer__content');
      if (this.getAttribute('open') === null || !sc || !this.querySelector('.b6d-rail--complete')) return () => {};
      const y = sc.scrollTop;
      const recs = this.querySelector('#cart-recommendations');
      const h = recs && recs.querySelector('li') ? recs.offsetHeight : 0;
      const held = CartDrawer.othersKeepCards() && recs && recs.querySelector('li:not([hidden])') ? recs.innerHTML : '';
      return () => {
        const next = this.querySelector('#cart-recommendations');
        if (next && held && !next.querySelector('li')) this.holdOthersCards(next, held);
        if (next && h && !next.querySelector('li')) {
          let watch = null;
          const release = () => {
            next.style.minHeight = '';
            if (watch) watch.disconnect();
          };
          next.style.minHeight = `${h}px`;
          if (window.MutationObserver) {
            watch = new MutationObserver(release);
            watch.observe(next, { childList: true, attributes: true, attributeFilter: ['data-recs-empty'] });
          }
          setTimeout(release, 10000);
        }
        if (next && next.cartRecsGo) next.cartRecsGo();
        const sc2 = this.querySelector('.drawer__content');
        if (sc2 && y) {
          sc2.scrollTop = y;
          requestAnimationFrame(() => { sc2.scrollTop = y; });
        }
      };
    }

    /** @returns {boolean} cart_others_keep_cards (layout/theme.liquid prints othersKeepCards only when it is on). */
    static othersKeepCards() {
      return !!(window.theme && window.theme.settings && window.theme.settings.othersKeepCards);
    }

    /**
     * B13B-3 (QA-B13 F2/F4, cart_others_keep_cards). After an Add inside the open drawer, "Others also bought" was an
     * empty box held at the old height for about 2.5 s until the new answer landed. The new, still empty box now shows
     * the previous cards (same markup, no skeleton, no new look) until the new answer replaces them (assets/main.js
     * ProductRecommendations.init: an answer with no card keeps them and asks again, as B11A-2 does on errors). A held
     * card whose product is now in the cart is hidden; when none is left the box is held empty as before.
     * @param {Element} next - The re-rendered #cart-recommendations.
     * @param {string} held - The previous box's markup.
     */
    holdOthersCards(next, held) {
      const handle = (a) => {
        const m = a && a.pathname ? a.pathname.match(/\/products\/([^/?#]+)/) : null;
        return m ? decodeURIComponent(m[1]) : '';
      };
      const inCart = new Set([...this.querySelectorAll('.cart-item a[href*="/products/"]')].map(handle));
      const own = next.innerHTML;
      next.innerHTML = held;
      next.querySelectorAll('li').forEach((li) => {
        if (inCart.has(handle(li.querySelector('a[href*="/products/"]')))) li.hidden = true;
      });
      if (!next.querySelector('li:not([hidden])')) {
        next.innerHTML = own;
        return;
      }
      next.removeAttribute('data-recs-empty');
      next.umbraKeepCards = true;
    }

    /**
     * Impulso A1 (2026-10-19): a re-render replaces the free shipping bar with a new element, so its fill would jump to
     * the new width. Call this before the markup is replaced and the returned function after: the new bar starts at the
     * old width and moves to its own two frames later, over the 0.7 s width transition in assets/cart-items.css.
     * No layout read, so no forced reflow.
     * @returns {Function}
     */
    holdShippingBar() {
      const oldBar = this.querySelector('#free-shipping-notice .fsn__bar');
      const from = oldBar ? oldBar.style.getPropertyValue('--progress') : '';
      return () => {
        const bar = this.querySelector('#free-shipping-notice .fsn__bar');
        if (!from || !bar) return;
        const to = bar.style.getPropertyValue('--progress');
        if (to === from) return;
        bar.style.setProperty('--progress', from);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => { bar.style.setProperty('--progress', to); });
        });
      };
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    /**
     * Dispatches an event to recalculate the data-css-var-heights on the page. Useful for
     * updating the height of the sticky cart buttons
     */
    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      return tmpl.content.querySelector(selector).innerHTML;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}

/**
 * Impulso A2 (2026-10-19): the "Complete your order" card (snippets/cart-drawer-cross-sell.liquid). The drawer render
 * prints this element only while a free shipping gap exists; it asks for the card once the drawer is open (nothing on
 * page load), by rendering the drawer's own section through Shopify's product recommendations for the most recently
 * added line's product. One request per cart state: answers are kept per product, intent and cart state, and an intent
 * that came back with no products for a product is not asked again for it. Complementary falls back to related, and
 * the related answer is read from the request the drawer's "Others also bought" rail already made (the rail renders
 * this same section through the related recommendations of the same product, so its answer carries this card's result
 * too); only without that rail does related cost a request of its own. A first open under the threshold therefore
 * costs at most one request more than the rail.
 * A box of the card's height is held from the first frame (assets/cart-items.css) so the rows below do not move when
 * the card lands; it collapses only when nothing came back.
 */
if (!customElements.get('cart-cross-sell')) {
  class CartCrossSell extends HTMLElement {
    connectedCallback() {
      this.drawer = this.closest('cart-drawer');
      if (!this.dataset.productId || !this.dataset.url) return;
      if (!this.drawer) {
        // 2026-10-26: the /cart page has no drawer. The card is on screen from the start, so it asks once when it comes
        // near the viewport (the one recommendations request the page's own rail already makes alongside).
        if (!('IntersectionObserver' in window)) {
          this.load();
          return;
        }
        this.observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load();
        }, { rootMargin: '200px' });
        this.observer.observe(this);
        return;
      }
      if (this.drawer.hasAttribute('open')) {
        this.load();
        return;
      }
      this.openHandler = () => this.load();
      this.drawer.addEventListener('on:cart-drawer:before-open', this.openHandler, { once: true });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.railWatch) this.railWatch.disconnect();
      if (this.openHandler && this.drawer) {
        this.drawer.removeEventListener('on:cart-drawer:before-open', this.openHandler);
      }
    }

    async load() {
      if (this.loading) return;
      this.loading = true;
      const { productId } = this.dataset;
      const intents = this.dataset.intent === 'related' ? ['related'] : ['complementary', 'related'];

      for (let i = 0; i < intents.length; i += 1) {
        const intent = intents[i];
        const noneKey = `${productId}|${intent}`;
        if (!CartCrossSell.none.has(noneKey)) {
          const key = `${noneKey}|${this.dataset.key}`;
          let html = CartCrossSell.cache.get(key);
          if (html === undefined) {
            try {
              let text = intent === 'related' ? await this.railText(productId) : null;
              if (text === null) {
                const response = await fetch(`${this.dataset.url}${intent}`);
                if (!response.ok) throw new Error(response.status);
                text = await response.text();
              }
              const tmpl = document.createElement('template');
              tmpl.innerHTML = text;
              const result = tmpl.content.querySelector('[data-xsell-result]');
              const card = result ? result.querySelector('[data-xsell-card]') : null;
              if (result && result.dataset.count === '0') CartCrossSell.none.add(noneKey);
              html = card ? card.outerHTML : '';
              CartCrossSell.cache.set(key, html);
            } catch (error) {
              html = '';
            }
          }
          if (html) {
            this.show(html);
            return;
          }
        }
      }

      if (this.isConnected) this.hidden = true;
    }

    /**
     * The answer of the drawer's rail (assets/main.js ProductRecommendations keeps its request as recsRequest) when it
     * is for the same product and the same cart state (the result's data-key), else null. Read one microtask late: on a cart change cart-items.js re-arms the rail right
     * after it re-renders the drawer, in the same task. A rail that has not started yet (a drawer re-rendered while open
     * waits for idle time) is started now: the request it was about to make anyway, only earlier.
     * @param {string} productId - The product the card is for.
     * @returns {Promise<?string>} The rail's response text, or null.
     */
    async railText(productId) {
      await null;
      const rail = (this.drawer || document).querySelector('#cart-recommendations');
      if (rail && !rail.recsRequest && rail.cartRecsGo) rail.cartRecsGo();
      const recs = rail ? rail.recsRequest : null;
      if (!recs || String(recs.productId) !== String(productId)) return null;
      try {
        const text = await recs.request;
        const tmpl = document.createElement('template');
        tmpl.innerHTML = text;
        const result = tmpl.content.querySelector('[data-xsell-result]');
        return result && result.dataset.key === this.dataset.key ? text : null;
      } catch (error) {
        return null;
      }
    }

    /**
     * TR23: the card's product is not repeated in the drawer's "Others also bought" rail. The rail prints two hidden spare
     * cards (snippets/recommendations.liquid, spares); the cards matching the shown product are removed and one spare
     * is revealed for each visible card removed, so the rail keeps its length when it can. When the rail's cards have not
     * arrived yet, one childList watch on the rail waits for them and then runs this once.
     */
    dedupeRail() {
      const card = this.querySelector('[data-xsell-card][data-pid]');
      const rail = this.drawer ? this.drawer.querySelector('#cart-recommendations') : null;
      if (!card || !rail) return;
      const items = rail.querySelectorAll('li[data-rec-pid]');
      if (!items.length) {
        if (this.railWatch || !('MutationObserver' in window)) return;
        this.railWatch = new MutationObserver(() => {
          if (!rail.querySelector('li[data-rec-pid]')) return;
          this.railWatch.disconnect();
          this.railWatch = null;
          this.dedupeRail();
        });
        this.railWatch.observe(rail, { childList: true });
        return;
      }
      let removed = 0;
      items.forEach((item) => {
        if (item.dataset.recPid !== card.dataset.pid) return;
        if (!item.hasAttribute('data-rec-spare')) removed += 1;
        item.remove();
      });
      const spares = rail.querySelectorAll('li[data-rec-spare]');
      for (let i = 0; i < removed && i < spares.length; i += 1) {
        spares[i].removeAttribute('hidden');
        spares[i].removeAttribute('data-rec-spare');
      }
    }

    /**
     * Puts the card in and, when it carries an Add button, makes sure the theme's product-form is defined.
     * @param {string} html - The card's markup.
     */
    show(html) {
      if (!this.isConnected) return;
      // Nodes are moved in, not re-parsed: on the /cart page the slot sits inside the cart's own <form>, and the HTML
      // parser would drop the card's own <form> there. DOM insertion keeps it.
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;
      this.replaceChildren(tmpl.content);
      this.setAttribute('data-ready', '');
      this.dedupeRail();
      this.bindOptions();
      this.bindPageAdd();
      const src = this.dataset.formSrc;
      if (src && this.querySelector('product-form') && !customElements.get('product-form')
        && !document.querySelector(`script[src="${src}"]`)) {
        loadScript(src).catch(() => {});
      }
    }
  }

  /**
   * 2026-10-26 (Mete): the card's option choosers. The variants come with the card itself (a JSON block printed by
   * snippets/cart-drawer-cross-sell.liquid from the response the card was already read from), so choosing asks for
   * nothing. A value is disabled when no available variant has it together with the other values now chosen; picking
   * a value whose partners are sold out moves those partners to the first available one. Add is disabled when the
   * chosen combination does not exist or is sold out.
   */
  CartCrossSell.prototype.bindOptions = function bindOptions() {
    const data = this.querySelector('script[data-xsell-variants]');
    const wrap = this.querySelector('[data-xsell-opts]');
    if (!data || !wrap) return;
    let variants;
    try { variants = JSON.parse(data.textContent); } catch (error) { return; }
    const groups = Array.from(wrap.querySelectorAll('[data-xsell-group]'));
    const idInput = this.querySelector('input[name="id"]');
    const skuInput = this.querySelector('input[name="sku"]');
    const addBtn = this.querySelector('[name="add"]');
    const img = this.querySelector('.cart-xsell__media img');
    const price = this.querySelector('.cart-xsell__price');
    const baseSrc = img ? img.getAttribute('src') : '';
    const baseSrcset = img ? img.getAttribute('srcset') : null;
    const match = (sel, ignore) => variants.filter((v) => sel.every((val, i) => i === ignore || v.o[i] === val));
    // sel holds every option position; single-valued options have no group and keep the first variant's value
    let sel;
    try { sel = JSON.parse(wrap.dataset.sel); } catch (error) { return; }

    const paint = () => {
      groups.forEach((g) => {
        const i = Number(g.dataset.idx);
        g.querySelectorAll('button').forEach((b) => {
          const ok = match(sel, i).some((v) => v.o[i] === b.dataset.value && v.a);
          b.disabled = !ok;
          b.setAttribute('aria-pressed', b.dataset.value === sel[i] ? 'true' : 'false');
        });
      });
      const hit = match(sel, -1)[0];
      if (hit && idInput) {
        idInput.value = hit.id;
        if (skuInput) skuInput.value = hit.k || '';
      }
      if (addBtn) {
        if (hit && hit.a) addBtn.removeAttribute('aria-disabled'); else addBtn.setAttribute('aria-disabled', 'true');
      }
      if (hit && price && hit.p) price.innerHTML = hit.p;
      /* B10A-4 (cart_rail_ship_line): "c" (the variant's price in cents) and the line's data-gap exist only with the
         setting on; the line shows for a variant that closes the gap and keeps its row (visibility) for one that does not. */
      const line = this.querySelector('.cart-xsell__line--v');
      if (hit && line && typeof hit.c === 'number') {
        const on = hit.c >= Number(line.dataset.gap);
        line.toggleAttribute('data-off', !on);
        if (on) line.removeAttribute('aria-hidden'); else line.setAttribute('aria-hidden', 'true');
      }
      if (img) {
        if (hit && hit.i) { img.src = hit.i; img.removeAttribute('srcset'); } else {
          img.src = baseSrc;
          if (baseSrcset) img.setAttribute('srcset', baseSrcset);
        }
      }
    };

    wrap.addEventListener('click', (event) => {
      const b = event.target.closest('button[data-value]');
      if (!b || b.disabled) return;
      const i = Number(b.closest('[data-xsell-group]').dataset.idx);
      sel[i] = b.dataset.value;
      // partners sold out with this value move to the first available variant that keeps it
      if (!match(sel, -1).some((v) => v.a)) {
        const first = variants.find((v) => v.a && v.o[i] === sel[i]);
        if (first) sel = first.o.slice();
      }
      paint();
    });
    paint();
  };

  /**
   * 2026-10-26: on the /cart page the slot sits inside the page's own <form>, so the card's form is nested and the
   * product-form script never sees a valid submit. There Add posts through JS, with the variant id the option state
   * already put in the hidden input, then reloads the page to show the new line. The drawer is not inside a form and
   * keeps its own product-form path untouched.
   */
  CartCrossSell.prototype.bindPageAdd = function bindPageAdd() {
    if (!this.closest('form')) return;
    const btn = this.querySelector('[name="add"]');
    const idInput = this.querySelector('input[name="id"]');
    if (!btn || !idInput) return;
    const errorBox = this.querySelector('.js-form-error');
    const showError = (message) => {
      if (!errorBox) return;
      errorBox.textContent = message || '';
      errorBox.hidden = !message;
    };
    /* FX1 C6b: the theme's own generic error strings (snippets/umbra-i18n.liquid); the inline English only covers a page without them */
    const genericError = () => {
      const t = window.umbraI18n || {};
      return t.atc_stepper_generic_error || t.atc_stock_check_failed || 'Something went wrong.';
    };
    btn.addEventListener('click', async (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (btn.getAttribute('aria-disabled') === 'true' || btn.dataset.busy) return;
      btn.dataset.busy = '1';
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: Number(idInput.value), quantity: 1 })
        });
        if (!response.ok) {
          /* FC C6: sold out and quantity limits answer 422 with a description, shown in the card's own alert box */
          let description = '';
          try { description = (await response.json()).description || ''; } catch (parseError) { description = ''; }
          showError(description || genericError());
          delete btn.dataset.busy;
          return;
        }
        showError('');
        window.location.reload();
      } catch (error) {
        showError(genericError());
        delete btn.dataset.busy;
      }
    }, true);
  };

  CartCrossSell.cache = new Map();
  CartCrossSell.none = new Set();
  customElements.define('cart-cross-sell', CartCrossSell);
}
